// Tiny base64url JWT inspector. Parses the `exp` claim out of a
// signed token without verifying the signature — the server is the
// authority; we just use this to decide whether to proactively
// refresh before firing an authenticated request.
//
// Returns unix seconds, or null on any malformed input. The hot
// path must never throw.

function decodeBase64Url(s: string): string | null {
  try {
    const padded = s + "===".slice((s.length + 3) % 4);
    const b64 = padded.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    // Convert to UTF-8 string so non-ASCII claims don't mangle.
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

export function getJwtExpSeconds(token: string | undefined | null): number | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const payloadJson = decodeBase64Url(parts[1] ?? "");
  if (!payloadJson) return null;
  try {
    const payload = JSON.parse(payloadJson) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

// `true` when the token is either already expired or will expire
// within `bufferSeconds` (default 60s). Unknown expiry is treated
// as stale so we refresh rather than send a doomed request.
export function isAccessTokenStale(
  token: string | undefined | null,
  bufferSeconds = 60,
): boolean {
  const exp = getJwtExpSeconds(token);
  if (exp === null) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - nowSeconds <= bufferSeconds;
}
